import React from 'react';
import { Shield, Trophy, CreditCard, Gift, AlertCircle, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const GamingPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white py-20">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="bg-yellow-600 p-4 rounded-full">
              <Trophy className="w-12 h-12 text-black" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Gaming & Competition Policy
          </h1>
          <p className="text-gray-400 text-lg">
            Fair play rules and competition guidelines for Fokatmain
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Last Updated: November 2024
          </p>
        </div>

        {/* Introduction */}
        <Card className="gaming-card mb-8">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <Shield className="w-8 h-8 text-yellow-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-xl font-bold text-yellow-400 mb-2">Welcome to Fokatmain</h3>
                <p className="text-gray-300 leading-relaxed">
                  Fokatmain is a skill-based gaming platform where players compete based on their gaming skills and scores. 
                  By participating in games and competitions on our platform, you agree to abide by the following policy. 
                  Please read carefully before playing.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 1: Game Modes */}
        <Card className="gaming-card mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Trophy className="w-6 h-6 text-yellow-400" />
              1. Game Modes
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-900/20 border border-blue-800/50 rounded-lg p-4">
              <h4 className="text-blue-400 font-semibold mb-2 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                Practice Mode (Free)
              </h4>
              <ul className="text-gray-300 text-sm space-y-1 ml-7">
                <li>• All games available for free practice</li>
                <li>• Unlimited attempts allowed</li>
                <li>• Build your skills and improve scores</li>
                <li>• Scores recorded but not eligible for prizes</li>
                <li>• No payment required</li>
              </ul>
            </div>

            <div className="bg-yellow-900/20 border border-yellow-800/50 rounded-lg p-4">
              <h4 className="text-yellow-400 font-semibold mb-2 flex items-center gap-2">
                <Trophy className="w-5 h-5" />
                Competition Mode (Paid)
              </h4>
              <ul className="text-gray-300 text-sm space-y-1 ml-7">
                <li>• Selected games opened by admin for competitions</li>
                <li>• Entry fee required (typically ₹10 per game)</li>
                <li>• Limited attempts based on payment (usually 3 attempts)</li>
                <li>• Highest score during competition period wins prize</li>
                <li>• Winners eligible for real gadgets and prizes</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Score-Based Competition */}
        <Card className="gaming-card mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Trophy className="w-6 h-6 text-yellow-400" />
              2. Score-Based Competition Rules
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 text-gray-300">
              <div>
                <h4 className="text-white font-semibold mb-2">2.1 How Winners Are Determined</h4>
                <ul className="space-y-1 ml-4 text-sm">
                  <li>• Winner is determined by <strong>highest valid score</strong> achieved during competition period</li>
                  <li>• Each player can make multiple attempts (as per their payment)</li>
                  <li>• Best score from all attempts counts for leaderboard</li>
                  <li>• All scores recorded with timestamp and player details</li>
                </ul>
              </div>

              <div>
                <h4 className="text-white font-semibold mb-2">2.2 Game Attempts</h4>
                <ul className="space-y-1 ml-4 text-sm">
                  <li>• Each ₹10 payment typically provides 3 game attempts</li>
                  <li>• New users: 1 trial attempt during payment verification (24 hours)</li>
                  <li>• Trusted users (3+ verified payments): Instant 3 attempts</li>
                  <li>• Unused attempts expire when competition ends</li>
                  <li>• No refund for unused attempts</li>
                </ul>
              </div>

              <div>
                <h4 className="text-white font-semibold mb-2">2.3 Tie-Breaking Rules</h4>
                <ul className="space-y-1 ml-4 text-sm">
                  <li>• In case of identical scores, earliest submission time wins</li>
                  <li>• Timestamp recorded by system is final and binding</li>
                  <li>• Players cannot dispute timestamp records</li>
                </ul>
              </div>

              <div>
                <h4 className="text-white font-semibold mb-2">2.4 Competition Duration</h4>
                <ul className="space-y-1 ml-4 text-sm">
                  <li>• Each competition has defined start and end time</li>
                  <li>• Duration set by admin (e.g., 24 hours, 48 hours, 1 week)</li>
                  <li>• Only scores submitted during competition period are valid</li>
                  <li>• Late submissions not accepted under any circumstances</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 3: Fair Play */}
        <Card className="gaming-card mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Shield className="w-6 h-6 text-yellow-400" />
              3. Fair Play & Anti-Cheating Policy
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 text-gray-300">
              <div className="bg-red-900/20 border border-red-800/50 rounded-lg p-4">
                <h4 className="text-red-400 font-semibold mb-2 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  Strictly Prohibited Activities
                </h4>
                <ul className="space-y-1 text-sm ml-2">
                  <li>❌ Using bots, scripts, or automated tools to play games</li>
                  <li>❌ Exploiting bugs or glitches for unfair advantage</li>
                  <li>❌ Creating multiple accounts to gain extra attempts</li>
                  <li>❌ Sharing account credentials or playing on behalf of others</li>
                  <li>❌ Manipulating scores or game data</li>
                  <li>❌ Using fake transaction IDs for payment</li>
                  <li>❌ Colluding with other players to fix results</li>
                </ul>
              </div>

              <div>
                <h4 className="text-white font-semibold mb-2">3.1 Fair Play Requirements</h4>
                <ul className="space-y-1 ml-4 text-sm">
                  <li>• All gameplay must be genuine and manual (human-played)</li>
                  <li>• One account per person (verified by phone/email)</li>
                  <li>• Honest payment submissions with real transaction IDs</li>
                  <li>• Play within game rules and time limits</li>
                  <li>• No external tools or modifications allowed</li>
                </ul>
              </div>

              <div>
                <h4 className="text-white font-semibold mb-2">3.2 Consequences of Cheating</h4>
                <ul className="space-y-1 ml-4 text-sm">
                  <li>• <strong>First Offense:</strong> Score disqualified, warning issued</li>
                  <li>• <strong>Second Offense:</strong> Account suspended for 30 days</li>
                  <li>• <strong>Third Offense:</strong> Permanent ban from platform</li>
                  <li>• No refund for entry fees if caught cheating</li>
                  <li>• Prize forfeited if winner found cheating</li>
                  <li>• Legal action for serious fraud attempts</li>
                </ul>
              </div>

              <div>
                <h4 className="text-white font-semibold mb-2">3.3 Admin Monitoring</h4>
                <ul className="space-y-1 ml-4 text-sm">
                  <li>• System automatically flags suspicious scores</li>
                  <li>• Admin reviews all winning scores before prize distribution</li>
                  <li>• Game data and timestamps analyzed for irregularities</li>
                  <li>• Admin decision on fair play is final and binding</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 4: Payment & Entry */}
        <Card className="gaming-card mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <CreditCard className="w-6 h-6 text-yellow-400" />
              4. Payment & Entry Fee Policy
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 text-gray-300">
              <div>
                <h4 className="text-white font-semibold mb-2">4.1 Entry Fee</h4>
                <ul className="space-y-1 ml-4 text-sm">
                  <li>• Standard entry fee: ₹10 per game (may vary by competition)</li>
                  <li>• Payment via UPI (PhonePe, GPay, Paytm, etc.)</li>
                  <li>• All payments processed in Indian Rupees (INR)</li>
                  <li>• Entry fee non-refundable once game attempts used</li>
                </ul>
              </div>

              <div>
                <h4 className="text-white font-semibold mb-2">4.2 Payment Process</h4>
                <ul className="space-y-1 ml-4 text-sm">
                  <li>1. Scan QR code or use UPI deep link to pay</li>
                  <li>2. Complete payment in your UPI app</li>
                  <li>3. Enter 12-digit UPI transaction ID on website</li>
                  <li>4. System validates transaction ID format and uniqueness</li>
                  <li>5. Admin verifies payment within 24 hours</li>
                  <li>6. Game attempts unlocked after verification</li>
                </ul>
              </div>

              <div>
                <h4 className="text-white font-semibold mb-2">4.3 Payment Verification</h4>
                <ul className="space-y-1 ml-4 text-sm">
                  <li>• <strong>New Users:</strong> 1 trial attempt during verification (24 hrs)</li>
                  <li>• <strong>Verified Users:</strong> Full 3 attempts after admin approval</li>
                  <li>• <strong>Trusted Users:</strong> Instant 3 attempts (3+ verified payments)</li>
                  <li>• Invalid transaction IDs rejected automatically</li>
                  <li>• Fake transaction IDs result in account flagging</li>
                </ul>
              </div>

              <div>
                <h4 className="text-white font-semibold mb-2">4.4 Refund Policy</h4>
                <ul className="space-y-1 ml-4 text-sm">
                  <li>• <strong>Eligible for Refund:</strong> Payment issues, technical errors before game played</li>
                  <li>• <strong>Not Eligible:</strong> Change of mind, poor performance, unused attempts</li>
                  <li>• Refund requests must be made within 48 hours</li>
                  <li>• Provide transaction ID and reason for refund</li>
                  <li>• Refund processed within 7-14 business days</li>
                  <li>• Contact: support@fokatmain.com</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 5: Prize Distribution */}
        <Card className="gaming-card mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Gift className="w-6 h-6 text-yellow-400" />
              5. Prize Distribution Policy
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 text-gray-300">
              <div>
                <h4 className="text-white font-semibold mb-2">5.1 Winner Announcement</h4>
                <ul className="space-y-1 ml-4 text-sm">
                  <li>• Winners announced on website and Instagram</li>
                  <li>• Typically within 24-48 hours of competition end</li>
                  <li>• Winner notified via email and phone (if provided)</li>
                  <li>• Leaderboard updated with final results</li>
                </ul>
              </div>

              <div>
                <h4 className="text-white font-semibold mb-2">5.2 Winner Verification</h4>
                <ul className="space-y-1 ml-4 text-sm">
                  <li>• Admin verifies payment transaction ID</li>
                  <li>• Reviews gameplay data for fair play compliance</li>
                  <li>• Confirms identity (name, phone, email match)</li>
                  <li>• Checks for multiple accounts or suspicious activity</li>
                  <li>• Winner must respond to verification within 7 days</li>
                </ul>
              </div>

              <div>
                <h4 className="text-white font-semibold mb-2">5.3 Prize Delivery</h4>
                <ul className="space-y-1 ml-4 text-sm">
                  <li>• Physical gadgets shipped to winner's address</li>
                  <li>• Shipping within India only</li>
                  <li>• Delivery within 7-14 business days after verification</li>
                  <li>• Winner must provide accurate shipping details</li>
                  <li>• No cash alternative for prizes</li>
                  <li>• Prize cannot be transferred to another person</li>
                </ul>
              </div>

              <div>
                <h4 className="text-white font-semibold mb-2">5.4 Prize Forfeiture</h4>
                <p className="text-sm mb-2">Prize will be forfeited if:</p>
                <ul className="space-y-1 ml-4 text-sm">
                  <li>• Winner found cheating or using unfair means</li>
                  <li>• Invalid or fake payment transaction ID</li>
                  <li>• Multiple accounts used by same person</li>
                  <li>• Winner does not respond within 7 days</li>
                  <li>• Provides incorrect or unreachable contact details</li>
                  <li>• Violates any terms of this policy</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 6: Disputes */}
        <Card className="gaming-card mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <AlertCircle className="w-6 h-6 text-yellow-400" />
              6. Disputes & Complaints
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 text-gray-300">
              <div>
                <h4 className="text-white font-semibold mb-2">6.1 How to Raise a Complaint</h4>
                <ul className="space-y-1 ml-4 text-sm">
                  <li>• Email: support@fokatmain.com</li>
                  <li>• Subject: "Competition Dispute - [Your Name]"</li>
                  <li>• Include: Transaction ID, Game name, Competition date, Issue details</li>
                  <li>• Attach: Screenshots or proof (if applicable)</li>
                  <li>• Complaint must be raised within 48 hours of competition end</li>
                </ul>
              </div>

              <div>
                <h4 className="text-white font-semibold mb-2">6.2 Resolution Process</h4>
                <ul className="space-y-1 ml-4 text-sm">
                  <li>• Acknowledgment within 24 hours</li>
                  <li>• Investigation within 3-5 business days</li>
                  <li>• Admin reviews all data (scores, timestamps, payments)</li>
                  <li>• Decision communicated via email</li>
                  <li>• Resolution time: 7-10 business days maximum</li>
                </ul>
              </div>

              <div>
                <h4 className="text-white font-semibold mb-2">6.3 Final Authority</h4>
                <ul className="space-y-1 ml-4 text-sm">
                  <li>• Fokat Main (Playedge Innovations) decision is final and binding</li>
                  <li>• No further appeals after final decision</li>
                  <li>• Admin reserves right to modify rules for fairness</li>
                  <li>• Any rule changes communicated on website</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 7: General Terms */}
        <Card className="gaming-card mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Shield className="w-6 h-6 text-yellow-400" />
              7. General Terms & Conditions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 text-gray-300 text-sm">
              <div>
                <h4 className="text-white font-semibold mb-2">7.1 Eligibility</h4>
                <ul className="space-y-1 ml-4">
                  <li>• Players must be 18 years or older to participate in paid competitions</li>
                  <li>• Valid Indian phone number and email required</li>
                  <li>• Residents of India only</li>
                  <li>• Employees and family members of Fokat Main not eligible for prizes</li>
                </ul>
              </div>

              <div>
                <h4 className="text-white font-semibold mb-2">7.2 Platform Rights</h4>
                <ul className="space-y-1 ml-4">
                  <li>• Fokat Main reserves right to cancel, modify, or suspend competitions</li>
                  <li>• Admin can disqualify players for rule violations</li>
                  <li>• Platform may change entry fees, game modes, or rules with notice</li>
                  <li>• Technical issues may result in competition extension or cancellation</li>
                </ul>
              </div>

              <div>
                <h4 className="text-white font-semibold mb-2">7.3 Liability</h4>
                <ul className="space-y-1 ml-4">
                  <li>• Fokat Main not liable for technical issues, internet problems, or device failures</li>
                  <li>• Players responsible for their own internet connection and device</li>
                  <li>• No compensation for lost opportunities due to technical issues</li>
                  <li>• Platform not responsible for third-party payment gateway issues</li>
                </ul>
              </div>

              <div>
                <h4 className="text-white font-semibold mb-2">7.4 Data & Privacy</h4>
                <ul className="space-y-1 ml-4">
                  <li>• Player data used for competition management only</li>
                  <li>• Winner names and scores may be published publicly</li>
                  <li>• Payment details secured and not shared with third parties</li>
                  <li>• Read our Privacy Policy for complete information</li>
                </ul>
              </div>

              <div>
                <h4 className="text-white font-semibold mb-2">7.5 Policy Updates</h4>
                <ul className="space-y-1 ml-4">
                  <li>• Policy may be updated periodically</li>
                  <li>• Changes communicated on website</li>
                  <li>• Continued participation implies acceptance of updated terms</li>
                  <li>• Check this page regularly for updates</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Section */}
        <Card className="gaming-card">
          <CardContent className="p-6">
            <div className="text-center">
              <h3 className="text-xl font-bold text-white mb-4">Questions or Concerns?</h3>
              <p className="text-gray-300 mb-4">
                If you have any questions about this Gaming & Competition Policy, please contact us:
              </p>
              <div className="space-y-2 text-sm text-gray-400">
                <p>📧 Email: support@fokatmain.com</p>
                <p>🌐 Website: fokatmain.com</p>
                <p>📱 Follow us on Instagram: @fokatmain</p>
              </div>
              <div className="mt-6 pt-6 border-t border-gray-800">
                <p className="text-xs text-gray-500">
                  © 2024 Fokat Main (Playedge Innovations). All rights reserved.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GamingPolicy;
